<template>
  <div
    v-loading="loading"
    class="max-w-360 m-auto w-full h-full flex flex-col justify-center items-center"
  >
    <div class="flex-1 flex justify-center items-center">
      <BaseLightPreview
        :has-brand-light="hasMultiLight"
        :band-light-styles="previewEffectStyles[LIGHTING_TARGET.BaseLightBand] ?? {}"
        :screen-light-styles="previewEffectStyles[LIGHTING_TARGET.BaseLightScreen] ?? {}"
        :enabled-lighting="showLightingSwitch ? enabledLighting : true"
      />
    </div>

    <div class="flex flex-col w-full gap-4.5 h-82 shrink-0 mb-2">
      <div class="h-10 shrink-0">
        <div v-if="hasMultiLight" class="p-0 flex gap-4 justify-center">
          <Button
            color="glass"
            :active="currentLightType === 'base-light-band'"
            class="h-10"
            @click="handleSetLightType('base-light-band')"
          >
            {{ $t('lighting.tabs.stripLight') }}
          </Button>
          <Button
            color="glass"
            :active="currentLightType === 'base-light-screen'"
            class="h-10"
            @click="handleSetLightType('base-light-screen')"
          >
            {{ $t('lighting.tabs.nameplateLight') }}
          </Button>
        </div>
      </div>
      <div class="h-67.5 w-full flex justify-center gap-2">
        <ColorPicker
          v-model="rgb"
          v-model:rgb-group="rgbGroup"
          :current-effect="currentEffect"
          :gradient-direction="gradientDirection"
          :is-colorful="isColorful"
          @change="setColor"
        />

        <EffectList
          :effects="effects"
          :enabled-lighting="enabledLighting"
          :show-lighting-switch="showLightingSwitch"
          :current-effect="currentEffect"
          :charging-state="chargingState"
          :sync-lighting="syncLighting"
          :show-sync-lighting="hasMultiLight"
          @update:enabled-lighting="setLightingEnabled"
          @update:current-effect="setEffect"
          @update:charing-state="setChargingLight"
          @update:sync-lighting="updateSyncLighting"
        />

        <div class="w-101 flex flex-col gap-2">
          <BrightnessSlider v-if="canEditBrightness" v-model="brightness" @change="setBrightness" />

          <SpeedSlider v-if="canEditSpeed" v-model="speed" @change="setSpeed" />

          <GradientSwitch
            v-if="canEditGradient"
            v-model="rgbGroup"
            :gradient-direction="gradientDirection"
            :current-effect-info="currentEffect"
            @change="setGradient"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { computed, ref } from 'vue'
  import { storeToRefs } from 'pinia'
  import { Button } from '@rapoo/element'
  import { BaseLightingTarget, LIGHTING_TARGET, type LightingCapability } from '@rapoo/device-sdk'
  import { useDeviceStore } from '@/stores/device'
  import { useBaseLighting } from '../composables/useBaseLighting'
  import { useRestoreChildDevice } from '../composables/useRestoreChildDevice'
  import BaseLightPreview from './components/BaseLightPreview.vue'
  import ColorPicker from './components/ColorPicker.vue'
  import EffectList from './components/EffectList.vue'
  import BrightnessSlider from './components/BrightnessSlider.vue'
  import SpeedSlider from './components/SpeedSlider.vue'
  import GradientSwitch from './components/GradientSwitch.vue'

  const deviceStore = useDeviceStore()
  const { currentDevice } = storeToRefs(deviceStore)
  useRestoreChildDevice()

  const currentLightType = ref<BaseLightingTarget>('base-light-band')

  const {
    effects,
    enabledLighting,
    showLightingSwitch,
    chargingState,
    syncLighting,
    currentEffect,
    previewEffectStyles,
    brightness,
    speed,
    rgb,
    rgbGroup,
    gradientDirection,
    loading,
    isColorful,
    canEditBrightness,
    canEditSpeed,
    canEditGradient,
    setLightingEnabled,
    updateSyncLighting,
    setEffect,
    setBrightness,
    setSpeed,
    setColor,
    setGradient,
    setChargingLight,
  } = useBaseLighting(currentLightType)

  const hasMultiLight = computed(() => {
    const lights = currentDevice.value?.manifest.capabilities.find(
      (item) => item.type === 'lighting',
    ) as LightingCapability | undefined
    if (lights) {
      return lights.config.effects.some(
        (effect) => effect.target === LIGHTING_TARGET.BaseLightScreen,
      )
    }

    return false
  })

  const handleSetLightType = (val: BaseLightingTarget) => {
    currentLightType.value = val
  }
</script>
